import { RevenueGridHeader, RevenueGridItem, setArrowIcon } from '@e2m-customer-portal/domains';
import { useLocale, useRevenueData } from '@e2m-customer-portal/hooks';
import { isControlEnergy, isSolar, MachineTechnology } from '@e2m-customer-portal/shared';
import { Collapse, Typography } from '@mui/material';
import { addMonths, format, startOfQuarter } from 'date-fns';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RevenueItem, RevenueScope } from '../../../generated/graphql';
import { ErrorComponent } from '@e2m-customer-portal/ui';

export interface QuarterlyRevenueTableProps {
  marketLocationId: string;
  selectedDate: Date;
  technologies: readonly MachineTechnology[];
  sidebarOffset?: number;
}

export function QuarterlyRevenueTable({
  marketLocationId,
  selectedDate,
  technologies,
  sidebarOffset = 0,
}: Readonly<QuarterlyRevenueTableProps>): ReactElement<QuarterlyRevenueTableProps> {
  const [t] = useTranslation();
  const locale = useLocale();

  const { data, error } = useRevenueData(RevenueScope.QUARTER, marketLocationId, selectedDate);

  if (!data || error) return <ErrorComponent error={error} />;

  const monthOfQuarter = (month: 0 | 1 | 2): Date => addMonths(startOfQuarter(selectedDate), month);

  const revenueItemsMap = new Map<string, RevenueItem>();

  data.items.map((item) =>
    revenueItemsMap.set(item.key, { key: item.key, amount: item?.amount, price: item?.price, sum: item?.sum }),
  );

  const [openSections, setOpenSections] = useState({
    RQ09: false,
    RQ06: isControlEnergy(technologies),
    RQ24: false,
    RQ07: false,
    RQ04: true,
  });

  const allSectionsOpen =
    (openSections.RQ09 ||
      openSections.RQ06 === !isControlEnergy(technologies) ||
      openSections.RQ24 ||
      openSections.RQ07) &&
    !Object.values(openSections).every(Boolean)
      ? null
      : Object.values(openSections).every(Boolean);

  const hasIntradayTrading =
    revenueItemsMap.get('RQ24')?.amount || revenueItemsMap.get('RQ24')?.price || revenueItemsMap.get('RQ24')?.sum;

  return (
    <div data-testid="quarterly-revenue-grid">
      <RevenueGridHeader
        allSectionsOpen={allSectionsOpen}
        showToggle={!isControlEnergy(technologies) && !isSolar(technologies)}
        sidebarOffset={sidebarOffset}
        toggleOpenSections={() =>
          setOpenSections((openSections) => {
            let sections = openSections;
            for (const key of Object.keys(openSections)) {
              if (['RQ09', 'RQ06', 'RQ24', 'RQ07'].includes(key)) continue;
              sections = { ...sections, [key]: true };
            }
            sections = {
              ...sections,
              RQ09: !allSectionsOpen,
              RQ06: !allSectionsOpen || isControlEnergy(technologies),
              RQ24: !allSectionsOpen,
              RQ07: !allSectionsOpen,
            };
            return sections;
          })
        }
      />
      <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
        {!isControlEnergy(technologies) && (
          <>
            <RevenueGridItem
              label={t('REVENUE_REPORT_TABLE_TOTAL_REVENUE')}
              variant="h3"
              className="py-4"
              indentLevel="0"
            />
            <RevenueGridItem
              item={revenueItemsMap.get('RQ02')}
              label={t('REVENUE_REPORT_TABLE_CONTRACT_PRICE')}
              contextInfo={t(
                isSolar(technologies)
                  ? 'REVENUE_REPORT_TABLE_HELP_CONTRACT_PRICE_SOLAR'
                  : 'REVENUE_REPORT_TABLE_HELP_CONTRACT_PRICE',
              )}
              isGrey
            />
            <RevenueGridItem
              item={revenueItemsMap.get('RQ08')}
              label={t('REVENUE_REPORT_TABLE_REDISPATCH')}
              contextInfo={t('REVENUE_REPORT_TABLE_HELP_REDISPATCH')}
            />
            {!isSolar(technologies) && (
              <>
                <RevenueGridItem
                  item={revenueItemsMap.get('RQ03')}
                  label={t('REVENUE_REPORT_TABLE_PROPORTIONAL_PROFIT')}
                  contextInfo={t('REVENUE_REPORT_TABLE_HELP_PROPORTIONAL_PROFIT')}
                  isGrey
                />
                <RevenueGridItem
                  label={t('REVENUE_REPORT_TABLE_SINGLE_ITEMS')}
                  variant="h3"
                  indentLevel="0"
                  className="py-4"
                />
                <RevenueGridItem
                  item={revenueItemsMap.get('RQ09')}
                  label={t('REVENUE_REPORT_TABLE_SCHEDULE_MANAGEMENT')}
                  contextInfo={t('REVENUE_REPORT_TABLE_HELP_SCHEDULE_MANAGEMENT')}
                  icon={setArrowIcon(openSections.RQ09, technologies)}
                  variant="h5"
                  onClick={() => setOpenSections({ ...openSections, RQ09: !openSections.RQ09 })}
                />
              </>
            )}
            {!isSolar(technologies) && (
              <Collapse in={openSections.RQ09} className="col-span-5">
                <RevenueGridItem
                  item={revenueItemsMap.get('RQ21')}
                  label={format(monthOfQuarter(0), 'LLLL', { locale })}
                  indentLevel="1"
                  isGrey
                />

                <RevenueGridItem
                  item={revenueItemsMap.get('RQ22')}
                  label={format(monthOfQuarter(1), 'LLLL', { locale })}
                  indentLevel="1"
                />

                <RevenueGridItem
                  item={revenueItemsMap.get('RQ23')}
                  label={format(monthOfQuarter(2), 'LLLL', { locale })}
                  indentLevel="1"
                  isGrey
                />
              </Collapse>
            )}
          </>
        )}

        {!isSolar(technologies) && (
          <RevenueGridItem
            item={revenueItemsMap.get('RQ06')}
            label={t('SHARED_PRODUCT_CONTROL_ENERGY')}
            contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY')}
            variant="h5"
            icon={setArrowIcon(openSections.RQ06, technologies)}
            onClick={() =>
              setOpenSections({ ...openSections, RQ06: isControlEnergy(technologies) || !openSections.RQ06 })
            }
          />
        )}
        {!isSolar(technologies) && (
          <Collapse in={openSections.RQ06} className="col-span-5">
            <RevenueGridItem
              item={revenueItemsMap.get('RQ18')}
              label={format(monthOfQuarter(0), 'LLLL', { locale })}
              indentLevel="1"
              isGrey
            />
            <RevenueGridItem
              item={revenueItemsMap.get('RQ19')}
              label={format(monthOfQuarter(1), 'LLLL', { locale })}
              indentLevel="1"
            />
            <RevenueGridItem
              item={revenueItemsMap.get('RQ20')}
              label={format(monthOfQuarter(2), 'LLLL', { locale })}
              indentLevel="1"
              isGrey
            />
          </Collapse>
        )}

        {hasIntradayTrading && (
          <>
            <RevenueGridItem
              item={revenueItemsMap.get('RQ24')}
              label={t('SHARED_PRODUCT_INTRADAY_TRADING')}
              contextInfo={t('QUARTERLY_REVENUE_REPORT_TABLE_HELP_INTRADAY_TRADING')}
              variant="h5"
              icon={setArrowIcon(openSections.RQ24, technologies)}
              onClick={() => setOpenSections({ ...openSections, RQ24: !openSections.RQ24 })}
            />
            <Collapse in={openSections.RQ24} className="col-span-5">
              <RevenueGridItem
                item={revenueItemsMap.get('RQ25')}
                label={format(monthOfQuarter(0), 'LLLL', { locale })}
                indentLevel="1.5"
                isGrey
              />
              <RevenueGridItem
                item={revenueItemsMap.get('RQ26')}
                label={format(monthOfQuarter(1), 'LLLL', { locale })}
                indentLevel="1.5"
              />
              <RevenueGridItem
                item={revenueItemsMap.get('RQ27')}
                label={format(monthOfQuarter(2), 'LLLL', { locale })}
                indentLevel="1.5"
                isGrey
              />
            </Collapse>
          </>
        )}
        {!isControlEnergy(technologies) && !isSolar(technologies) && (
          <>
            <RevenueGridItem
              item={revenueItemsMap.get('RQ07')}
              label={t('REVENUE_REPORT_TABLE_EXTRA_PROFIT')}
              contextInfo={t('REVENUE_REPORT_TABLE_HELP_EXTRA_PROFIT')}
              variant="h5"
              icon={setArrowIcon(openSections.RQ07, technologies)}
              onClick={() => setOpenSections({ ...openSections, RQ07: !openSections.RQ07 })}
            />
            <Collapse in={openSections.RQ07} className="col-span-5">
              <RevenueGridItem
                item={revenueItemsMap.get('RQ04')}
                label={t('REVENUE_REPORT_TABLE_PROPORTIONAL_PROFIT')}
                contextInfo={t('REVENUE_REPORT_TABLE_HELP_PROPORTIONAL_PROFIT')}
                variant="body3"
                indentLevel="1"
                icon={setArrowIcon(openSections.RQ04, technologies)}
                onClick={() => setOpenSections({ ...openSections, RQ04: !openSections.RQ04 })}
              />
              <Collapse in={openSections.RQ04} className="col-span-5">
                <RevenueGridItem
                  item={revenueItemsMap.get('RQ15')}
                  label={format(monthOfQuarter(0), 'LLLL', { locale })}
                  indentLevel="1.5"
                  isGrey
                />

                <RevenueGridItem
                  item={revenueItemsMap.get('RQ16')}
                  label={format(monthOfQuarter(1), 'LLLL', { locale })}
                  indentLevel="1.5"
                />

                <RevenueGridItem
                  item={revenueItemsMap.get('RQ17')}
                  label={format(monthOfQuarter(2), 'LLLL', { locale })}
                  indentLevel="1.5"
                  isGrey
                />
              </Collapse>
            </Collapse>
          </>
        )}
      </div>
      <div data-testid="revenue-report-disclaimer" className="p-4">
        {!isControlEnergy(technologies) && !isSolar(technologies) && (
          <>
            <Typography paragraph variant="body2">
              {t('REVENUE_REPORT_TABLE_DISCLAIMER_NOTICE_ONE')}
            </Typography>
            <Typography paragraph variant="body2">
              {t('REVENUE_REPORT_TABLE_DISCLAIMER_NOTICE_TWO')}
            </Typography>
          </>
        )}
        <Typography paragraph variant="body2">
          {t(
            isSolar(technologies)
              ? 'REVENUE_REPORT_TABLE_DISCLAIMER_SOLAR'
              : isControlEnergy(technologies)
                ? 'REVENUE_REPORT_TABLE_DISCLAIMER_CONTROL_ENERGY'
                : 'REVENUE_REPORT_TABLE_DISCLAIMER',
          )}
        </Typography>
      </div>
    </div>
  );
}
